import { ReactElement, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import { Crud, CrudFormComponent, CrudMode, CrudProps, notification, rejectionError, transformCrudFormErrors, ValidationError } from '@ohpen/enso-ui';

import { getApiErrorMessage } from '@pmp/utils';

import { getReplaceSchema } from '../regionsBdmsFormConfig';
import { toCrudFieldErrors } from '../utils';

type SelectOption = { label: string; value: string };
type ReplaceFormValues = { replacementId: string };

interface Props {
  title: string;
  explanation: string;
  replacementLabelKey: string;
  requiredMessage: string;
  options: SelectOption[];
  onReplace: (replacementId: string) => Promise<unknown>;
  successMessage: (replacementId: string) => string;
  onClose: () => void;
  refreshTable: () => void;
}

export const ReplaceForm = ({
  title,
  explanation,
  replacementLabelKey,
  requiredMessage,
  options,
  onReplace,
  successMessage,
  onClose,
  refreshTable,
}: Props): ReactElement => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  const renderExplanation = useCallback((): ReactNode => <Typography.Paragraph>{explanation}</Typography.Paragraph>, [explanation]);

  const onConfirm = useCallback(
    async (values: ReplaceFormValues): Promise<ValidationError[] | void> => {
      try {
        await onReplace(values.replacementId);
        notification().success({ title: successMessage(values.replacementId) });
        refreshTable();
      } catch (error) {
        const fieldErrors = toCrudFieldErrors(error);
        if (fieldErrors) return transformCrudFormErrors(fieldErrors);
        return Promise.reject(rejectionError(getApiErrorMessage(error)));
      }
    },
    [onReplace, successMessage, refreshTable],
  );

  const crudProps: CrudProps<ReplaceFormValues> = useMemo(
    () => ({
      ref,
      open,
      schema: getReplaceSchema(replacementLabelKey, requiredMessage, options, renderExplanation),
      title,
      labels: { confirm: t('lender.regionsBdms.labels.replace') },
      initialValues: null,
      mode: CrudMode.CREATE,
      onClose: handleOnClose,
      onConfirm,
    }),
    [open, title, replacementLabelKey, requiredMessage, options, renderExplanation, t, handleOnClose, onConfirm],
  );

  return <Crud {...crudProps} />;
};
